// src/pages/FarmMark.js

/**
 * FarmMark — Impact and gamification page.
 *
 * DESKTOP: three clear columns
 *   1. My Impact link (compact, leads to the full My Impact experience)
 *   2. FarmMark leaderboard (clickable names, @username, IP aligned)
 *   3. How to earn IP (Contribute / Buy-sell-trust / Feedback)
 *
 * MOBILE: compact header, prominent leaderboard, Filter/Controls sheet.
 * No My Impact panel on mobile — it lives behind the existing route.
 *
 * Preserves all existing business logic, reward rules, and data flow.
 */

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { WifiOff, ArrowLeft, User } from "lucide-react";
import { Link } from "react-router-dom";
import FarmMarkLeaderboard from "../components/FarmMark/FarmMarkLeaderboard";
import EarnIPTips from "../components/FarmMark/EarnIPTips";

export default function FarmMark() {
  const { t } = useTranslation("farmMark");
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.innerWidth >= 1024;
  });

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  useEffect(() => {
    const onResize = () => {
      if (typeof window !== "undefined") setIsDesktop(window.innerWidth >= 1024);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
      {/* Sticky header */}
      <header className="sticky top-0 z-30 border-b border-gray-200 bg-white dark:border-neutral-800 dark:bg-neutral-900">
        <div className="mx-auto flex h-12 max-w-7xl items-center gap-2 px-3 sm:px-4">
          <Link
            to="/me"
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 dark:text-gray-300 dark:hover:bg-neutral-800"
            aria-label={t("common:back", { defaultValue: "Back" })}
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </Link>
          <h1 className="text-base font-semibold text-gray-900 dark:text-white truncate">
            {t("page.title")}
          </h1>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4">
        {!isOnline && (
          <div className="mb-3 p-2.5 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 inline-flex items-center gap-2 text-xs">
            <WifiOff className="w-3.5 h-3.5" />
            {t("offline")}
          </div>
        )}

        {isDesktop ? (
          /* ───── DESKTOP: three columns ───── */
          <div className="grid grid-cols-[260px_minmax(0,1fr)_280px] gap-5">
            {/* Column 1 — My Impact link (compact) */}
            <aside className="space-y-3">
              <Link
                to="/my-impact"
                className="group flex items-center gap-3 rounded-xl border border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-3 hover:border-green-500 dark:hover:border-green-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
              >
                <div className="w-9 h-9 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-green-700 dark:text-green-400" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate group-hover:text-green-700 dark:group-hover:text-green-400 transition-colors">
                    {t("myImpact.title")}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {t("myImpact.subtitle")}
                  </div>
                </div>
              </Link>
            </aside>

            {/* Column 2 — Leaderboard */}
            <section>
              <FarmMarkLeaderboard />
            </section>

            {/* Column 3 — How to earn IP */}
            <aside>
              <EarnIPTips />
            </aside>
          </div>
        ) : (
          /* ───── MOBILE ───── */
          <div>
            <FarmMarkLeaderboard />
          </div>
        )}
      </main>
    </div>
  );
}